import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../styles/GlobalStyles';
import { classService } from '../../services/classService';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';
import { Loader } from '../../components/Loader';
import { toast } from 'react-toastify';

const Container = styled.div``;

const Title = styled.h1`
  color: ${colors.primary};
  margin-bottom: 30px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px ${colors.shadow};
`;

const ClassTitle = styled.h3`
  color: ${colors.primary};
  margin-bottom: 10px;
`;

const ClassInfo = styled.div`
  color: ${colors.text};
  margin-bottom: 8px;
  font-size: 14px;
`;

const Progress = styled.div`
  width: 100%;
  height: 12px;
  background-color: ${colors.secondary};
  border-radius: 6px;
  overflow: hidden;
  margin: 15px 0;
`;

const ProgressBar = styled.div`
  height: 100%;
  background-color: ${props => {
    if (props.percentage >= 100) return colors.error;
    if (props.percentage >= 80) return colors.warning;
    return colors.success;
  }};
  width: ${props => props.percentage}%;
  transition: width 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 11px;
  font-weight: bold;
`;

const MemberList = styled.div`
  margin-top: 15px;
`;

const MemberItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background-color: ${colors.background};
  border-radius: 6px;
  margin-bottom: 8px;
`;

const ProfilePhoto = styled.img`
  width: 35px;
  height: 35px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid ${colors.primary};
`;

const PhotoPlaceholder = styled.div`
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background-color: ${colors.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.primary};
  font-weight: bold;
  font-size: 12px;
`;

const MemberInfo = styled.div`
  flex: 1;
`;

const MemberName = styled.div`
  font-weight: 500;
  color: ${colors.text};
  font-size: 14px;
`;

const MemberEmail = styled.div`
  font-size: 12px;
  color: ${colors.textLight};
`;

const DaySection = styled.div`
  margin-bottom: 30px;
`;

const DayHeader = styled.div`
  background: ${colors.primary};
  color: white;
  padding: 15px 20px;
  border-radius: 8px 8px 0 0;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 0;
`;

const DayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  background: white;
  padding: 20px;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 2px 8px ${colors.shadow};
`;

export const AdminClassOccupancy = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(getStartOfWeek(new Date()));

  useEffect(() => {
    fetchClasses();
  }, [currentWeekStart]);

  // Get start of week (Monday)
  function getStartOfWeek(date) {
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(date);
    monday.setDate(date.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
  }

  // Get classes for the current week
  const getWeekClasses = () => {
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(currentWeekStart.getDate() + 7);
    
    const expandedClasses = [];
    const now = new Date();
    const fiveHoursAgo = new Date(now.getTime() - (5 * 60 * 60 * 1000));
    
    classes.forEach(classItem => {
      if (classItem.isRecurring) {
        // For recurring classes, generate instances for each day in the week
        for (let i = 0; i < 7; i++) {
          const date = new Date(currentWeekStart);
          date.setDate(currentWeekStart.getDate() + i);
          
          const classDate = new Date(classItem.date + 'T00:00:00');
          
          // Only show if it's the same day of week and on or after the class start date
          if (date.getDay() === classDate.getDay() && date >= classDate) {
            const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            
            // Check if class is not too old (5 hours after class time)
            const classDateTime = new Date(`${dateStr}T${classItem.startTime || classItem.time || '00:00'}`);
            if (classDateTime >= fiveHoursAgo) {
              expandedClasses.push({
                ...classItem,
                date: dateStr,
                displayDate: date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' })
              });
            }
          }
        }
      } else {
        // For non-recurring classes, check if it's in this week
        const classDate = new Date(classItem.date + 'T00:00:00');
        if (classDate >= currentWeekStart && classDate < weekEnd) {
          // Check if class is not too old (5 hours after class time)
          const classDateTime = new Date(`${classItem.date}T${classItem.startTime || classItem.time || '00:00'}`);
          if (classDateTime >= fiveHoursAgo) {
            expandedClasses.push({
              ...classItem,
              displayDate: classDate.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' })
            });
          }
        }
      }
    });
    
    // Sort by date and time
    return expandedClasses.sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date);
      if (dateCompare !== 0) return dateCompare;
      return (a.startTime || a.time || '').localeCompare(b.startTime || b.time || '');
    });
  };

  // Get classes grouped by day for daily view
  const getDailyClasses = () => {
    const weekClasses = getWeekClasses();
    const dailyGroups = {};
    
    weekClasses.forEach(classItem => {
      const date = classItem.date;
      if (!dailyGroups[date]) {
        dailyGroups[date] = [];
      }
      dailyGroups[date].push(classItem);
    });
    
    // Convert to array and sort by date
    return Object.entries(dailyGroups)
      .map(([date, classes]) => ({
        date,
        classes: classes.sort((a, b) => (a.startTime || a.time || '').localeCompare(b.startTime || b.time || ''))
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  };

  const goToPreviousWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(currentWeekStart.getDate() - 7);
    setCurrentWeekStart(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(currentWeekStart.getDate() + 7);
    setCurrentWeekStart(newDate);
  };

  const goToToday = () => {
    setCurrentWeekStart(getStartOfWeek(new Date()));
  };

  const getWeekRange = () => {
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(currentWeekStart.getDate() + 6);

    const startDay = currentWeekStart.getDate();
    const endDay = weekEnd.getDate();

    if (currentWeekStart.getMonth() === weekEnd.getMonth()) {
      const month = currentWeekStart.toLocaleDateString('tr-TR', { month: 'long' });
      const year = currentWeekStart.getFullYear();
      return `${startDay} - ${endDay} ${month} ${year}`;
    } else {
      const startMonth = currentWeekStart.toLocaleDateString('tr-TR', { month: 'long' });
      const endMonth = weekEnd.toLocaleDateString('tr-TR', { month: 'long' });
      const year = currentWeekStart.getFullYear();
      return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
    }
  };

  const fetchClasses = async () => {
    try {
      setLoading(true);
      // Get classes with occupancy data for the current week
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(currentWeekStart.getDate() + 7);
      
      const expandedClasses = [];
      const now = new Date();
      const fiveHoursAgo = new Date(now.getTime() - (5 * 60 * 60 * 1000));
      
      // Get all classes first
      const allClasses = await classService.getAllClasses();
      
      for (const classItem of allClasses) {
        if (classItem.isRecurring) {
          // For recurring classes, generate instances for each day in the week
          for (let i = 0; i < 7; i++) {
            const date = new Date(currentWeekStart);
            date.setDate(currentWeekStart.getDate() + i);
            
            const classDate = new Date(classItem.date + 'T00:00:00');
            
            // Only show if it's the same day of week and on or after the class start date
            if (date.getDay() === classDate.getDay() && date >= classDate) {
              const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
              
              // Check if class is not too old (5 hours after class time)
              const classDateTime = new Date(`${dateStr}T${classItem.startTime || classItem.time || '00:00'}`);
              if (classDateTime >= fiveHoursAgo) {
                // Get actual bookings for this specific class and date
                const allBookings = await classService.getClassBookings(classItem.id);
                const bookingsForThisDate = allBookings.filter(booking => 
                  booking.classDate === dateStr && booking.status === 'approved'
                );
                
                expandedClasses.push({
                  ...classItem,
                  date: dateStr,
                  currentBookings: bookingsForThisDate.length,
                  availableSpots: classItem.capacity - bookingsForThisDate.length,
                  isFull: bookingsForThisDate.length >= classItem.capacity,
                  displayDate: date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' })
                });
              }
            }
          }
        } else {
          // For non-recurring classes, check if they fall within the current week
          const classDate = new Date(classItem.date + 'T00:00:00');
          if (classDate >= currentWeekStart && classDate < weekEnd) {
            const classDateTime = new Date(`${classItem.date}T${classItem.startTime || classItem.time || '00:00'}`);
            if (classDateTime >= fiveHoursAgo) {
              // Get actual bookings for this specific class and date
              const allBookings = await classService.getClassBookings(classItem.id);
              const bookingsForThisDate = allBookings.filter(booking => 
                booking.classDate === classItem.date && booking.status === 'approved'
              );
              
              expandedClasses.push({
                ...classItem,
                currentBookings: bookingsForThisDate.length,
                availableSpots: classItem.capacity - bookingsForThisDate.length,
                isFull: bookingsForThisDate.length >= classItem.capacity,
                displayDate: classDate.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' })
              });
            }
          }
        }
      }
      
      setClasses(expandedClasses);
    } catch (error) {
      toast.error('Dersler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleViewBookings = async (classItem) => {
    try {
      setSelectedClass(classItem);
      setModalOpen(true);
      setLoadingBookings(true);
      
      // Get bookings for this specific class and date
      const allBookings = await classService.getClassBookings(classItem.id);
      
      // Filter bookings for the specific date
      const filteredBookings = allBookings.filter(booking => 
        booking.classDate === classItem.date
      );
      
      setBookings(filteredBookings);
    } catch (error) {
      toast.error('Rezervasyonlar yüklenirken hata oluştu');
    } finally {
      setLoadingBookings(false);
    }
  };

  if (loading) {
    return <Loader fullScreen />;
  }

  return (
    <Container>
      <Title>Ders Dolulukları</Title>

      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px', 
        background: 'white', 
        padding: '15px', 
        borderRadius: '8px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <Button onClick={goToPreviousWeek} size="small" style={{ minWidth: '80px', fontSize: '12px', padding: '8px 12px' }}>← Önceki</Button>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: '8px', 
          flex: '1',
          minWidth: '140px',
          maxWidth: '200px'
        }}>
          <h3 style={{ 
            margin: 0, 
            color: '#2C5F2D', 
            fontSize: '16px', 
            textAlign: 'center', 
            fontWeight: '600',
            lineHeight: '1.2'
          }}>
            {getWeekRange()}
          </h3>
          <Button onClick={goToToday} size="small" variant="secondary" style={{ fontSize: '12px', padding: '6px 12px', minWidth: '60px' }}>Bugün</Button>
        </div>
        <Button onClick={goToNextWeek} size="small" style={{ minWidth: '80px', fontSize: '12px', padding: '8px 12px' }}>Sonraki →</Button>
      </div>

      {getDailyClasses().map((dayData, dayIndex) => (
        <DaySection key={dayData.date}>
          <DayHeader>
            {new Date(dayData.date + 'T00:00:00').toLocaleDateString('tr-TR', { 
              day: 'numeric', 
              month: 'long', 
              year: 'numeric', 
              weekday: 'long' 
            })}
          </DayHeader>
          <DayGrid>
            {dayData.classes.map((classItem, index) => {
              const percentage = (classItem.currentBookings / classItem.capacity) * 100;
              
              return (
                <Card key={`${classItem.id}-${classItem.date}`}>
                  <ClassTitle>{classItem.title}</ClassTitle>
                  
                  <ClassInfo>👨‍🏫 <strong>Eğitmen:</strong> {classItem.instructor}</ClassInfo>
                  <ClassInfo>🕐 <strong>Saat:</strong> {classItem.startTime || classItem.time} {classItem.endTime ? `- ${classItem.endTime}` : ''}</ClassInfo>
                  
                  <Progress>
                    <ProgressBar percentage={percentage}>
                      {percentage > 20 && `${percentage.toFixed(0)}%`}
                    </ProgressBar>
                  </Progress>

                  <ClassInfo>
                    <strong>Doluluk:</strong> {classItem.currentBookings} / {classItem.capacity} Kişi
                  </ClassInfo>
                  <ClassInfo>
                    <strong>Boş Yer:</strong> {classItem.availableSpots} Kişi
                  </ClassInfo>

                  {classItem.currentBookings > 0 && (
                    <Button 
                      size="small" 
                      fullWidth 
                      onClick={() => handleViewBookings(classItem)}
                      style={{ marginTop: '10px' }}
                    >
                      Katılımcıları Gör
                    </Button>
                  )}
                </Card>
              );
            })}
          </DayGrid>
        </DaySection>
      ))}

      {getDailyClasses().length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <p style={{ color: colors.textLight }}>
            Bu hafta için ders bulunmamaktadır.
          </p>
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`${selectedClass?.title} - Katılımcılar`}
        maxWidth="600px"
      >
        {loadingBookings ? (
          <Loader />
        ) : (
          <>
            <ClassInfo style={{ marginBottom: '20px' }}>
              📅 {selectedClass?.date} - 🕐 {selectedClass?.time}
            </ClassInfo>
            <ClassInfo style={{ marginBottom: '20px' }}>
              <strong>Toplam Katılımcı:</strong> {bookings.length}
            </ClassInfo>
            
            <MemberList>
              {bookings.length === 0 ? (
                <p style={{ textAlign: 'center', color: colors.textLight }}>
                  Henüz katılımcı yok
                </p>
              ) : (
                bookings.map(booking => (
                  <MemberItem key={booking.id}>
                    {booking.memberDetails?.profilePhoto ? (
                      <ProfilePhoto 
                        src={booking.memberDetails.profilePhoto} 
                        alt={`${booking.memberDetails.firstName} ${booking.memberDetails.lastName}`}
                      />
                    ) : (
                      <PhotoPlaceholder>
                        {booking.memberDetails?.firstName?.[0]}
                        {booking.memberDetails?.lastName?.[0]}
                      </PhotoPlaceholder>
                    )}
                    <MemberInfo>
                      <MemberName>
                        {booking.memberDetails?.firstName} {booking.memberDetails?.lastName}
                      </MemberName>
                      <MemberEmail>{booking.memberDetails?.email}</MemberEmail>
                      {booking.memberDetails?.phone && (
                        <MemberEmail>📞 {booking.memberDetails.phone}</MemberEmail>
                      )}
                    </MemberInfo>
                  </MemberItem>
                ))
              )}
            </MemberList>
          </>
        )}
      </Modal>
    </Container>
  );
};

